import { FC } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Form, useForm } from 'components/form'
import {
  EMAIL_RULE,
  EmailFormField,
  fileRule,
  FIRST_NAME_RULE,
  FirstNameFormField,
  LAST_NAME_RULE,
  LastNameFormField,
  LINKEDIN_PROFILE,
  LINKEDIN_OPTIONAL_RULE,
  LinkedinNicknameFormField,
  InternationalPhoneFormField,
  PHONE_RULE,
} from 'components/form-field'
import { FormNameType } from 'utils/types'
import { LoadFileFormField } from 'components/form-field/LoadFileFormField/LoadFileFormField'
import { SubmitFormButton } from 'components/submit-form-button'
import { yup } from 'components/form/yup'
import { useDialogContext } from 'components/dialog/utils'

import classes from './join-us-form.module.css'
import { MIME_TYPES } from '../../const/file-mime-type'
import { nest } from '../../api'
import { CandidateCreateModel } from '../../api/nest/candidates/types'

const JOIN_US_MODAL_FORM_NAME: FormNameType<CandidateCreateModel> = {
  email: 'email',
  firstName: 'firstName',
  lastName: 'lastName',
  linkedin: 'linkedin',
  phone: 'phone',
  resume: 'resume',
  vacancyId: 'vacancyId',
  vacancySubtitle: 'vacancySubtitle',
  vacancyTitle: 'vacancyTitle',
}

const VALIDATION_SCHEMA = yup.object().shape({
  [JOIN_US_MODAL_FORM_NAME.email]: EMAIL_RULE,
  [JOIN_US_MODAL_FORM_NAME.firstName]: FIRST_NAME_RULE,
  [JOIN_US_MODAL_FORM_NAME.lastName]: LAST_NAME_RULE,
  [JOIN_US_MODAL_FORM_NAME.phone]: PHONE_RULE.required(),
  [JOIN_US_MODAL_FORM_NAME.linkedin]: LINKEDIN_OPTIONAL_RULE.optional(),
  [JOIN_US_MODAL_FORM_NAME.resume]: fileRule({ maxFileSize: 2 }).required(),
})

export interface JoinUsFormProps {
  vacancyId: number | string
  vacancyTitle: string
  vacancySubtitle: string
}

export const JoinUsForm: FC<JoinUsFormProps> = ({ vacancyId, vacancyTitle, vacancySubtitle }) => {
  const formMethods = useForm(VALIDATION_SCHEMA, {
    defaultValues: {
      [JOIN_US_MODAL_FORM_NAME.vacancyId]: vacancyId,
      [JOIN_US_MODAL_FORM_NAME.vacancyTitle]: vacancyTitle,
      [JOIN_US_MODAL_FORM_NAME.vacancySubtitle]: vacancySubtitle,
    },
    mode: 'onChange',
  })
  const { close } = useDialogContext()

  const onSubmit: SubmitHandler<CandidateCreateModel> = async (data) => {
    await nest.candidates.create({
      ...data,
      linkedin: data.linkedin?.replace(new RegExp(`^${LINKEDIN_PROFILE.url}`), ''),
    })
    toast.success('Resume sent. Thank you for your interest!')
    formMethods.reset()
    close()
  }

  return (
    <Form formMethods={formMethods} onSubmit={onSubmit} className={classes.form}>
      <FirstNameFormField name={JOIN_US_MODAL_FORM_NAME.firstName} />
      <LastNameFormField name={JOIN_US_MODAL_FORM_NAME.lastName} />
      <EmailFormField name={JOIN_US_MODAL_FORM_NAME.email} />
      <InternationalPhoneFormField name={JOIN_US_MODAL_FORM_NAME.phone} formMethods={formMethods} />
      <LinkedinNicknameFormField
        name={JOIN_US_MODAL_FORM_NAME.linkedin}
        label="Linkedin (optional)"
      />
      <LoadFileFormField
        label="Attach Resume/CV"
        info={
          <ul className={classes.fileHelper}>
            <li>Only .pdf, .doc, .docx</li>
            <li>Max size 3Mb</li>
          </ul>
        }
        name={JOIN_US_MODAL_FORM_NAME.resume}
        accept={[MIME_TYPES['.pdf'], MIME_TYPES['.doc'], MIME_TYPES['.docx']]}
      />
      <SubmitFormButton className={classes.btn}>Send</SubmitFormButton>
    </Form>
  )
}
